<template>
  <div class="tasks-summary" @click="goToTasks">
    <h3 class="tasks-title">Tasks</h3>
    <div class="stats-bar">
      <div class="stat">
        <span class="icon">🎯</span>
        {{ totalTasks }} Total
      </div>
      <div class="divider">|</div>
      <div class="stat">
        <span class="icon">⚠️</span>
        {{ highPriorityCount }} High Priority
      </div>
      <div class="divider">|</div>
      <div class="stat">
        <span class="icon">⏰</span>
        {{ overdueTasks }} Overdue
      </div>
    </div>

    <div class="priority-items" v-if="priorityTasks.length">
      <h4>Priority Items:</h4>
      <div 
        v-for="task in priorityTasks" 
        :key="task.id" 
        class="task-item"
        @click.stop="goToTask(task.id)"
      >
        <div class="task-content">
          <span class="task-icon">{{ getTaskIcon(task) }}</span>
          <span class="task-description">
            {{ task.title }}
            <span class="task-related" v-if="task.related_to_user_name">[{{ task.related_to_user_name }}]</span>
          </span>
        </div>
        <span class="task-due">{{ getRelativeDueDate(task.due_date) }}</span>
      </div>
    </div>
    <div v-else class="no-tasks">
      No priority tasks at the moment
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { isToday, isTomorrow, differenceInDays, parseISO, isPast } from 'date-fns';

export default {
  name: 'TasksSummary',
  
  props: {
    tasks: {
      type: Array,
      required: true,
      default: () => []
    }
  },

  setup(props) {
    const router = useRouter();

    const totalTasks = computed(() => 
      Array.isArray(props.tasks) ? props.tasks.filter(task => task?.status !== 'completed').length : 0
    );
    
    const highPriorityCount = computed(() => 
      Array.isArray(props.tasks) ? props.tasks.filter(task => task?.priority === 'high' && task?.status !== 'completed').length : 0
    );

    const overdueTasks = computed(() => 
      Array.isArray(props.tasks) ? props.tasks.filter(task => 
        task?.due_date && 
        isPast(parseISO(task.due_date)) && 
        task?.status !== 'completed'
      ).length : 0
    );

    const priorityTasks = computed(() => {
      if (!Array.isArray(props.tasks)) return [];
      
      // Sort tasks by priority and due date
      return [...props.tasks]
        .filter(task => task && typeof task === 'object' && task.status !== 'completed') // Filter out completed tasks
        .sort((a, b) => {
          // First by priority
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
          if (priorityDiff !== 0) return priorityDiff;

          // Then by due date
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(a.due_date) - new Date(b.due_date);
        })
        .slice(0, 3); // Get top 3 priority tasks
    });

    const getTaskIcon = (task) => {
      switch (task.type) {
        case 'call':
          return '📞';
        case 'review':
          return '📋';
        case 'followup':
          return '⚠️';
        default:
          return '📝';
      }
    };

    const getRelativeDueDate = (date) => {
      if (!date) return '';
      const parsedDate = parseISO(date);
      
      if (isToday(parsedDate)) return '(Today)';
      if (isTomorrow(parsedDate)) return '(Tomorrow)';
      
      const days = differenceInDays(parsedDate, new Date());
      if (days < 0) return '(Overdue)';
      return `(In ${days} days)`;
    };

    const goToTasks = () => {
      router.push('/tasks');
    };

    const goToTask = (taskId) => {
      router.push(`/tasks/${taskId}`);
    };

    return {
      totalTasks,
      highPriorityCount,
      overdueTasks,
      priorityTasks,
      getTaskIcon,
      getRelativeDueDate,
      goToTasks,
      goToTask
    };
  }
};
</script>

<style scoped>
.tasks-summary {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tasks-title {
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2c3e50;
}

.stats-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
  margin-bottom: 1rem;
}

.stat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #666;
}

.divider {
  color: #ddd;
  margin: 0 0.5rem;
}

.icon {
  font-size: 1.1rem;
}

.priority-items {
  margin-top: 1rem;
}

h4 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 0.9rem;
}

.task-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
  text-align: left;
}

.task-content {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex: 1;
}

.task-description {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.task-icon {
  font-size: 1rem;
  width: 1.5rem;
}

.task-related {
  color: #666;
  margin-left: 0.5rem;
  font-size: 0.9rem;
}

.task-due {
  font-size: 0.8rem;
  color: #666;
}

.no-tasks {
  color: #666;
  font-size: 0.9rem;
  text-align: center;
  padding: 1rem 0;
}

@media (max-width: 768px) {
  .stats-bar {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }

  .divider {
    display: none;
  }
}
</style>
